/**
 * 2026-02-18 Bug #1 (Dirk) — Receipt scan tile.
 *
 * Two surfaces in one route:
 *   • Tech / rep / anyone: a "Snap a receipt" capture flow that drops
 *     the file into `receipt_inbox` (via /api/receipt-inbox) for admin
 *     review.
 *   • Admin / manager / owner: the inbox itself, with allocate-to-
 *     project and dismiss controls.
 *
 * No modals (per the contract). Inline expand for allocate.
 */
import { useEffect, useState } from "react";
import {
  ReceiptText, Camera, Loader2, ArrowLeft, X,
  CheckCircle2, ChevronRight, Building2,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { uploadUrl } from "../lib/uploads";
import SearchableSelect from "../components/SearchableSelect";

export default function ReceiptScanPage() {
  const [me, setMe] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [allocating, setAllocating] = useState(null); // {id} when inline allocate is open

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe({}));
    loadInbox();
  }, []);

  const loadInbox = async () => {
    setLoadingInbox(true);
    try {
      const r = await api.get("/receipt-inbox");
      setInbox(r.data?.items || []);
    } catch (_e) { /* may be 403 for techs — that's OK */ }
    setLoadingInbox(false);
  };

  const isStaff = me && ["owner", "admin", "manager"].includes(me.role);

  const onSnap = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("note", "");
        await api.post("/receipt-inbox", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,
        });
        ok += 1;
      }
      toast.success(`${ok} receipt${ok === 1 ? "" : "s"} sent for allocation.`);
      if (isStaff) loadInbox();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Receipt upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="p-3 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="receipt-scan-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/" className="hv-btn hv-btn-ghost text-xs" data-testid="receipt-scan-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </Link>
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <ReceiptText className="w-5 h-5 text-[#F59E0B]" /> Receipt scan
        </h1>
      </div>

      {/* Capture card */}
      <div className="hv-card p-4 space-y-2" data-testid="receipt-scan-capture">
        <div className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
          Quick capture
        </div>
        <p className="text-xs text-[#647087]">
          Snap a slip on the road — fuel, hardware, parts. Admin will allocate it
          to a project. Camera or pick from gallery — both work.
        </p>
        <label className="hv-btn hv-btn-primary cursor-pointer w-fit" data-testid="receipt-scan-snap">
          {uploading
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
            : <><Camera className="w-4 h-4" /> Snap a receipt</>}
          <input
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            multiple
            onChange={onSnap}
            className="hidden"
            disabled={uploading}
          />
        </label>
      </div>

      {/* Admin inbox */}
      {isStaff && (
        <div className="hv-card p-4 space-y-2" data-testid="receipt-scan-inbox">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
              Pending allocation
            </div>
            <span className="text-[10px] text-[#647087] font-mono">
              {inbox.length} item{inbox.length === 1 ? "" : "s"}
            </span>
          </div>
          {loadingInbox && (
            <div className="text-xs text-[#647087]">
              <Loader2 className="w-3 h-3 inline animate-spin" /> Loading…
            </div>
          )}
          {!loadingInbox && inbox.length === 0 && (
            <div className="text-xs text-[#647087]" data-testid="receipt-inbox-empty">
              Nothing pending. New receipts will appear here.
            </div>
          )}
          {!loadingInbox && inbox.length > 0 && (
            <div className="space-y-2">
              {inbox.map((r) => (
                <ReceiptRow
                  key={r.id}
                  receipt={r}
                  isOpen={allocating === r.id}
                  onToggle={() => setAllocating((cur) => cur === r.id ? null : r.id)}
                  onAllocated={() => { setAllocating(null); loadInbox(); }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReceiptRow({ receipt, isOpen, onToggle, onAllocated }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [category, setCategory] = useState("supplies");
  const [amount, setAmount] = useState(receipt.amount_hint || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    api.get("/projects?limit=200").then((r) => {
      setProjects(r.data?.items || r.data || []);
    }).catch(() => setProjects([]));
  }, [isOpen]);

  const allocate = async () => {
    if (!projectId) { toast.error("Pick a project."); return; }
    setBusy(true);
    try {
      await api.post(`/receipt-inbox/${receipt.id}/allocate`, {
        project_id: projectId,
        category,
        amount: amount ? Number(amount) : null,
        note,
      });
      toast.success("Receipt allocated.");
      onAllocated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Allocate failed.");
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    if (!window.confirm("Dismiss this receipt? It will move to the trash.")) return;
    try {
      await api.delete(`/receipt-inbox/${receipt.id}`);
      onAllocated();
    } catch (e) {
      toast.error("Dismiss failed.");
    }
  };

  return (
    <div className="border border-white/5 rounded p-2" data-testid={`receipt-row-${receipt.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {receipt.file_url && receipt.content_type?.startsWith("image/") ? (
          <img
            src={uploadUrl(receipt.file_url) || receipt.file_url}
            alt="receipt"
            className="w-12 h-12 object-cover rounded border border-white/10"
          />
        ) : (
          <div className="w-12 h-12 rounded border border-white/10 flex items-center justify-center text-[#647087]">
            <ReceiptText className="w-5 h-5" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate" data-testid={`receipt-row-title-${receipt.id}`}>
            {receipt.original_filename || "Receipt"}
          </div>
          <div className="text-[10px] text-[#647087]">
            from {receipt.uploaded_by_name || "user"} · {new Date(receipt.created_at).toLocaleString("en-ZA")}
          </div>
        </div>
        <button onClick={onToggle} className="hv-btn hv-btn-ghost text-xs" data-testid={`receipt-row-allocate-${receipt.id}`}>
          {isOpen ? <><X className="w-3 h-3" /> Close</> : <><ChevronRight className="w-3 h-3" /> Allocate</>}
        </button>
        <button onClick={dismiss} className="text-rose-400 hover:text-rose-300 text-xs px-2" data-testid={`receipt-row-dismiss-${receipt.id}`}>
          Dismiss
        </button>
      </div>
      {isOpen && (
        <div className="mt-2 border-t border-white/5 pt-2 space-y-2" data-testid={`receipt-allocate-form-${receipt.id}`}>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Project</label>
            <SearchableSelect
              value={projectId || ""}
              onChange={setProjectId}
              options={projects.map((p) => ({
                id: p.id,
                label: p.title || p.short_id || p.id,
                sublabel: p.customer_name || "",
                searchTerms: [p.short_id, p.title, p.customer_name].filter(Boolean),
              }))}
              placeholder="— Pick a project —"
              testId={`receipt-allocate-project-${receipt.id}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="hv-input mt-1 w-full"
                data-testid={`receipt-allocate-category-${receipt.id}`}
              >
                <option value="supplies">Supplies</option>
                <option value="fuel">Fuel</option>
                <option value="parts">Parts</option>
                <option value="tools">Tools</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Amount (R)</label>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder="0.00"
                className="hv-input mt-1 w-full"
                data-testid={`receipt-allocate-amount-${receipt.id}`}
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Note (optional)</label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="hv-input mt-1 w-full"
              placeholder="e.g. Diesel for Joburg trip"
              data-testid={`receipt-allocate-note-${receipt.id}`}
            />
          </div>
          <button
            onClick={allocate}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid={`receipt-allocate-save-${receipt.id}`}
          >
            <CheckCircle2 className="w-4 h-4" />
            {busy ? "Saving…" : "Allocate to project"}
          </button>
        </div>
      )}
    </div>
  );
}
